import React from 'react';
import { useTranslation } from 'react-i18next';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import NotificationBell from '../components/NotificationBell';
import SavedCardBanner from '../components/SavedCardBanner';
import FoundersRenewalReminder from '../components/FoundersRenewalReminder';
import FoundersChecklist from '../components/FoundersChecklist';
import FoundersPricingNote from '../components/FoundersPricingNote';
import FeaturedUpgradeCard from '../components/FeaturedUpgradeCard';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import BusinessShareControl from '../components/BusinessShareControl';
import { Building2, LogOut, AlertCircle, Eye, CalendarDays } from 'lucide-react';

import useBusinessDashboard from './dashboard/useBusinessDashboard';
import { planAllows } from './dashboard/useBusinessDashboard';
import { SubscriptionSection } from './dashboard/SubscriptionSection';
import { AnalyticsSection } from './dashboard/AnalyticsSection';
import { ClosureSection } from './dashboard/ClosureSection';
import { EventsSection } from './dashboard/EventsSection';
import { OffersSection } from './dashboard/OffersSection';
import { PricePromotionsSection } from './dashboard/PricePromotionsSection';
import { BoostHistorySection } from './dashboard/BoostHistorySection';
import BusinessProfileSection from './dashboard/BusinessProfileSection';
import { BusinessGuideSection } from './dashboard/BusinessGuideSection';
import TrialBanner from '../components/TrialBanner';

const BusinessDashboard = () => {
  const { t } = useTranslation();
  const db = useBusinessDashboard(t);

  if (db.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!db.business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('businessNotFound')}</p>
          <button onClick={() => db.navigate('/')} className="mt-4 text-purple-600">{t('goHome')}</button>
        </div>
      </div>
    );
  }

  const statusInfo = db.getStatusInfo();
  const canEvents   = planAllows(db.business?.subscriptionPlan, 'events');
  const canOffers   = planAllows(db.business?.subscriptionPlan, 'offers');
  const canBoosts   = planAllows(db.business?.subscriptionPlan, 'boost');

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6" />
              <h1 className="text-xl font-bold">{t('businessDashboard')}</h1>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell variant="on-purple" />
              <LanguageToggle />
              <button onClick={db.handleLogout} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* 60-day free trial banner — renders only for trial / paused statuses */}
        <TrialBanner />

        {/* Iter 119: Founding Business renewal reminder — appears at 30/14/7
            day windows before foundersFreeUntil. Returns null otherwise. */}
        <FoundersRenewalReminder />

        {/* Iter 120: Founders Activation Checklist — encourages first-week
            success (profile · photos · 1st event · 1st offer · 1st follower).
            Renders only for founders plan and only until dismissed. */}
        <FoundersChecklist />

        {/* Iter 118: Optional dismissible card-on-file prompt for one-click
            boosts. Hidden until user saves a card or dismisses. */}
        <SavedCardBanner />

        {/* Message */}
        {db.message.text && (
          <div
            className={`p-4 rounded-lg ${db.message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}
            data-testid="dashboard-toast"
            role="status"
          >
            {db.message.text}
          </div>
        )}

        {/* Preview My Listing */}
        <button
          onClick={() => window.open(`/business/${db.business.id}?preview=true`, '_blank')}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-white border-2 border-purple-200 text-purple-700 rounded-xl font-semibold text-sm hover:bg-purple-50 hover:border-purple-400 transition-all shadow-sm"
          data-testid="preview-listing-btn"
        >
          <Eye className="h-5 w-5" />
          {t('previewMyListing', 'Preview My Listing')}
        </button>

        {/* Calendar — reuses the existing shared calendar (/calendar). Business
            accounts are normal authenticated users, so this opens their existing
            calendar; no separate business calendar is created. */}
        <button
          onClick={() => db.navigate('/calendar')}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-purple-600 text-white rounded-xl font-semibold text-sm hover:bg-purple-700 transition-all shadow-sm"
          data-testid="business-calendar-btn"
        >
          <CalendarDays className="h-5 w-5" />
          {t('quickAccess.calendar', 'Calendar')}
        </button>

        <SubscriptionSection
          business={db.business} statusInfo={statusInfo}
          retrying={db.retrying} handleRetryPayment={db.handleRetryPayment}
          handleOpenPortal={db.handleOpenPortal} handleUpgrade={db.handleUpgrade} portalLoading={db.portalLoading}
        />

        {/* Iter 196 — Owner shortcut to copy / share their public business
            link without having to navigate to their public page first. */}
        {db.business?.id && db.business?.approvalStatus === 'approved' && (
          <BusinessShareControl
            businessId={db.business.id}
            businessName={db.business.businessName}
            businessLogo={db.business.logo || db.business.photos?.[0]}
            businessCity={db.business.city}
          />
        )}

        {/* Iter 193 — Featured Business Membership upgrade card (Phase 1 only:
            owner-facing upgrade + limit lift + 3 video link slots; consumer-facing
            badges/carousel/video player intentionally deferred to validate
            willingness to pay first). */}
        <FeaturedUpgradeCard />

        {/* Iter 194 — Persistent "Install App" entry for owners. Hides itself
            automatically once the app is running in standalone (already
            installed). Opens native prompt or shared help modal. */}
        <InstallAppMenuItem surface="dashboard-sidebar" variant="card" testId="dashboard-install-app-card" />

        <BusinessGuideSection
          business={db.business}
          handleOpenPortal={db.handleOpenPortal}
          handleUpgrade={db.handleUpgrade}
          portalLoading={db.portalLoading}
        />

        <AnalyticsSection
          analytics={db.analytics}
          analyticsPeriod={db.analyticsPeriod}
          changeAnalyticsPeriod={db.changeAnalyticsPeriod}
        />

        <ClosureSection
          closures={db.closures}
          loading={db.closuresLoading}
          submitting={db.closureSubmitting}
          onCreate={db.createClosure}
          onUpdate={db.updateClosure}
          onDelete={db.deleteClosure}
          onReopen={db.reopenClosure}
        />

        {canEvents && (
        <div id="events-section" className="space-y-3">
        {/* Iter 124: Subtle pricing reassurance — free listing/events/offers
            during the Founders period, boosts optional. Founders-plan only. */}
        <FoundersPricingNote subscriptionPlan={db.business?.subscriptionPlan} />
        <EventsSection
          business={db.business}
          myEvents={db.myEvents} eventsLoading={db.eventsLoading}
          showEventForm={db.showEventForm} setShowEventForm={db.setShowEventForm}
          eventSubmitting={db.eventSubmitting} posterUploading={db.posterUploading}
          eventForm={db.eventForm} setEventForm={db.setEventForm}
          eventErrors={db.eventErrors} setEventErrors={db.setEventErrors}
          editingEventId={db.editingEventId} setEditingEventId={db.setEditingEventId}
          archiveConfirm={db.archiveConfirm} setArchiveConfirm={db.setArchiveConfirm}
          posterInputRef={db.posterInputRef}
          handlePosterUpload={db.handlePosterUpload}
          handleEventSubmit={db.handleEventSubmit} handleEventUpdate={db.handleEventUpdate}
          openEditForm={db.openEditForm} handleArchiveEvent={db.handleArchiveEvent}
          handleDuplicateEvent={db.handleDuplicateEvent}
          restoreEvent={db.restoreEvent}
          fetchMyEvents={db.fetchMyEvents}
          setBoostModal={db.setBoostModal} setSelectedTier={db.setSelectedTier}
        />
        </div>
        )}

        {canOffers && (
        <div id="offers-section">
        <OffersSection
          myOffers={db.myOffers} offersLoading={db.offersLoading}
          showOfferForm={db.showOfferForm} setShowOfferForm={db.setShowOfferForm}
          offerSubmitting={db.offerSubmitting}
          offerForm={db.offerForm} setOfferForm={db.setOfferForm}
          offerErrors={db.offerErrors} setOfferErrors={db.setOfferErrors}
          editingOfferId={db.editingOfferId} setEditingOfferId={db.setEditingOfferId}
          handleOfferSubmit={db.handleOfferSubmit} handleDeleteOffer={db.handleDeleteOffer}
          openEditOffer={db.openEditOffer} handleOfferUpdate={db.handleOfferUpdate}
          handleDuplicateOffer={db.handleDuplicateOffer}
          restoreOffer={db.restoreOffer}
          handleOfferImageUpload={db.handleOfferImageUpload}
          offerImageUploading={db.offerImageUploading}
          offerImageInputRef={db.offerImageInputRef}
          boostTiers={db.boostTiers} boostModal={db.boostModal} setBoostModal={db.setBoostModal}
          selectedTier={db.selectedTier} setSelectedTier={db.setSelectedTier}
          boostLoading={db.boostLoading} handleBoostCheckout={db.handleBoostCheckout}
        />
        </div>
        )}

        {/* Price-2-Day Sponsored Promotions (Stage 1) — visible to approved
            businesses. Isolated from the genuine price comparison engine. */}
        {db.business?.approvalStatus === 'approved' && (
          <PricePromotionsSection business={db.business} />
        )}

        {canBoosts && (
        <BoostHistorySection
          boostHistory={db.boostHistory}
          setBoostModal={db.setBoostModal} setSelectedTier={db.setSelectedTier}
        />
        )}

        <div id="business-profile-section">
        <BusinessProfileSection
          business={db.business}
          editing={db.editing} setEditing={db.setEditing}
          editForm={db.editForm} setEditForm={db.setEditForm}
          handleSaveEdit={db.handleSaveEdit}
          uploading={db.uploading}
          handlePhotoUpload={db.handlePhotoUpload} handleDeletePhoto={db.handleDeletePhoto} handleReorderPhotos={db.handleReorderPhotos}
          handleLogoUpload={db.handleLogoUpload}
          fileInputRef={db.fileInputRef} navigate={db.navigate}
        />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default BusinessDashboard;
